import React, { useCallback, useEffect } from "react";
import { useParams } from "react-router";
import { Col, Flex } from "@amzn/stencil-react-components/layout";
import { Text as StencilText } from "@amzn/stencil-react-components/text";
import { IconCheck } from "../../components/icons/check";
import { H1, Text } from "../../components/typography";
import { defaultTextColor, newUIBackgroundColor } from "../../config/palette";
import { TasksGroup } from "../../components/task";
import { TaskGroupAggregator } from "../../components/task/ui-redesign";
import { Stage } from "../../helpers/get-settings";
import headerImageSrc from "./header.png";
import * as adobeAnalytics from "../../utility/adobe-analytics";
import { Sda } from "../../components/sda";
import { ManageApplicationCard } from "../../components/manage-app-card";
import { isDragonstone } from "../../helpers/url-params";
import { AppointmentCard } from "../../components/appointment-card";
import { ApplicationStatus } from "../../components/application-status";
import { DocumentStatusTask } from "../../components/document-satus";
import { DocumentUploadTask } from "../tasks/document/entry";
import {
  RIGHT_TO_WORK_STATUS_ENDPOINT,
  buildUrl,
  MEDICAL_CHECK_STATUS_ENDPOINT,
  ZAPPOS_URL,
  NEW_HIRE_VIDEO_URL,
  TAKE_BADGE_PHOTO_START_URL,
  PERSONAL_INFORMATION_COLLECTION_URL,
  BACKGROUND_CHECK_STATUS_ENDPOINT,
  BACKGROUND_CHECK_STATUS_AIR_ENDPOINT,
  DRUG_TEST_STATUS_ENDPOINT,
  BADGE_PHOTO_STATUS_ENDPOINT,
  NEW_HIRE_VIDEO_STATUS_ENDPOINT,
  ATOZ_ENDPOINT,
  ATOZ_STATUS_ENDPOINT,
  DOCUMENT_STATUS_ENDPOINT,
  ONBASE_DOCUMENT_STATUS_ENDPOINT,
  CAAS_API_URL,
  BACKGROUND_CHECK_STATUS_SF_ENDPOINT,
} from "../../config/urls";
import { Link } from "@amzn/stencil-react-components/link";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { I9Task } from "../../components/i9-task";
import { StatusEnum } from "../../hoc/with-status";
import { PICTask } from "../../components/pic-task";
import { AppointmentDetailsTask } from "../../components/appointment-details-task";
import useArb from "../../hooks/use-arb";
import { ZapposPurchaseTask } from "../../components/zappos-purchase-promotion";
import { AtoZStatusTask } from "../../components/atoz-status";
import {
  Status,
  StatusIndicator,
} from "@amzn/stencil-react-components/status-indicator";
import {
  isTaskEnabled,
  Task,
  TaskGroup,
  ORDER_YOUR_SAFETY_SHOE,
  EMPLOYMENT_PAPERWORK,
  PRE_HIRE_APPOINTMENT_GROUP,
  FIRST_DAY_GROUP,
  CREATE_EMPLOYEE_A_TO_Z_ACCOUNT,
  I_9_DOCUMENT,
  NEW_HIRE_VIDEO,
  BADGE_PHOTO,
} from "../../config/tasks";
import { getMessageValues, MessageSet } from "../../config/task-message-config";
import {
  getConfigurations,
  getWorkAuthorizationDocumentList,
  isCustomerService,
  isTaskEnabledByAppConfig,
  processConfigurations,
} from "../../utility/app-config-helper";
import {
  AIR_BGC_IN_SCREENING_CHECK_TASK_ID,
  APP_CONFIG_LOCAL_STORAGE_TTL,
  APP_CONFIG_UPDATED_TIME,
  APPOINTMENT_DETAILS_TASK_ID,
  BADGE_PHOTO_TASK_ID,
  BGC_CHECK_IN_SCREENING_TASK_ID,
  BP_DURING_APPOINTMENT_TASK_ID,
  DOCUMENT_TASK_ID,
  DT_IN_SCREENING_CHECK_TASK_ID,
  EMPLOYMENT_PAPERWORK_TASK_ID,
  HIDE_ATOZ_TASK,
  I9_TASK_ID,
  MEDICAL_CHECK_IN_SCREENING_TASK_ID,
  MYSP_TASK_ID,
  NHE_VIDEO_TASK_ID,
  PACKAGE_I9_TASK_ID,
  PIC_IN_SCREENING_CHECK_TASK_ID,
} from "../../config/appConfigConstants";
import { deleteCachedRequest } from "../../helpers/fetcher";
import { ScreeningResults } from "../../components/screening-results";
import { DisplayNewUIComponent } from "../../utility/new-ui-toggle";
import video from "./icon/video.png";
import shoes from "./icon/shoes.png";
import form from "./icon/form.png";
import badge from "./icon/badge.png";
import computer from "./icon/computer.png";
import { CAAS_STATUS_TO_ACTIVITY_STATUS } from "../../../src/components/i9-task/i9-data";
import { useBreakpoints } from "@amzn/stencil-react-components/responsive";
import { load } from "../../utility/local-storage";

interface ChecklistRouteProps {
  stage?: Stage;
}

export const ChecklistRoute = ({ stage }: ChecklistRouteProps) => {
  const { applicationId, requisitionId, candidateId } = useParams<{
    applicationId: string;
    requisitionId: string;
    candidateId: string;
  }>();
  const [appConfigLoaded, setAppConfigLoaded] = React.useState(false);

  useEffect(() => {
    deleteCachedRequest();
    getASHConfigurations();
    window.ASH_CHECKLIST_isChecklistReferral = true;
    adobeAnalytics.addPageLoadMetric(window, applicationId, "Ash thank you", {
      jobId: requisitionId,
      isDragonstone: isDragonstone(requisitionId),
    });
  }, [appConfigLoaded, applicationId, requisitionId]);

  const bundle = useArb("HVHChecklistPantherResource");
  const taskMessages: MessageSet = getMessageValues();

  const getASHConfigurations = useCallback(async () => {
    try {
      const lastUpdated = localStorage.getItem(
        APP_CONFIG_UPDATED_TIME + applicationId
      );
      const now = new Date();
      let lastUpdatedWithTTL = now;
      if (lastUpdated) {
        lastUpdatedWithTTL = new Date(lastUpdated);
        lastUpdatedWithTTL.setSeconds(
          lastUpdatedWithTTL.getSeconds() + APP_CONFIG_LOCAL_STORAGE_TTL
        );
      }

      if (!lastUpdated || lastUpdatedWithTTL < now) {
        //the last updated app config has expired on browser
        console.log("Refreshed configs from AppConfig");
        const result = await getConfigurations();
        await processConfigurations(result, applicationId);
        localStorage.setItem(
          APP_CONFIG_UPDATED_TIME + applicationId,
          now.toString()
        );
        await getWorkAuthorizationDocumentList(applicationId);
      }
    } catch (e) {
      if (e instanceof Error) {
        console.error(
          `Failed to call AWS AppConfig. Applying default value for all ASH Configs. Error message ${e.message}`
        );
      }
    } finally {
      setAppConfigLoaded(true);
    }
  }, []);

  const supportLink = (
    <Link target="_blank" href={taskMessages.acceptableDocLink}>
      {bundle.getMessage(taskMessages.acceptableDocText)}
    </Link>
  );

  const welcomeMessage = bundle.getMessage("Checklist-MainPage-WelcomeMessage");
  const reminderMessage = bundle.getMessage(
    "Checklist-MainPage-ReminderMessage"
  );
  const title = bundle.getMessage("Checklist-MainPage-Title");
  const subTitle1 = bundle.getMessage("Checklist-MainPage-SubTitle1");
  const subTitle2 = bundle.getMessage("Checklist-MainPage-SubTitle2");
  const subTitle3 = bundle.getMessage("Checklist-MainPage-SubTitle3");
  const newSubTitle1 = bundle.getMessage("Checklist-MainPage-NewSubTitle1");
  const newSubTitle3 = bundle.getMessage("Checklist-MainPage-NewSubTitle3");
  const videoTitle = bundle.getMessage("Checklist-MainPage-Video-Title");
  const screenTitle = bundle.getMessage("Checklist-MainPage-Screen-Title");
  const picTitle = bundle.getMessage("Checklist-MainPage-PIC-Title");
  /*const paperworkTitle = bundle.getMessage(
    "Checklist-MainPage-Paperwork-Title"
  );*/
  const packageI9Title = bundle.getMessage(
    "Checklist-MainPage-PackageI9-Title"
  );

  const completeI9Title = bundle.getMessage(
    "Checklist-MainPage-CompleteI9-Title"
  );
  const badgePhotoTitle = bundle.getMessage(
    "Checklist-MainPage-BadgePhoto-Title"
  );
  const appointmentTitle = bundle.getMessage(
    "Checklist-MainPage-Appointment-Title"
  );
  const badgePhotoDetails = bundle.getMessage("Checklist-MainPage-BP-Details");
  const badgePhotoButtonText = bundle.getMessage(
    "Checklist-MainPage-BP-ButtonText"
  );
  const videoDetails = bundle.getMessage("Checklist-MainPage-NHE-Details");
  const videoButtonText = bundle.getMessage(
    "Checklist-MainPage-NHE-ButtonText"
  );
  const packI9Detail1 = bundle.formatMessage(
    "Checklist-MainPage-PackI9-Details1",
    {
      supportLink: supportLink,
    }
  );

  const bpDuringAppointment = bundle.getMessage(
    "Checklist-MainPage-BP-DuringAppointment"
  );
  const duringAppointment = bundle.formatMessage(
    taskMessages.duringAppointment,
    {
      supportLink: supportLink,
    }
  );

  /*const paperDetails = bundle.getMessage(
    "Checklist-MainPage-PaperWork-Details"
  );*/

  const bgcTitle = bundle.getMessage("Checklist-MainPage-BGC-Title");
  const airBgcTitle = bundle.getMessage("Checklist-MainPage-AIR-BGC-Title");
  const dtTitle = bundle.getMessage("Checklist-MainPage-DT-Title");
  const medicalCheckTitle = bundle.getMessage(
    "Checklist-MainPage-MedicalCheck-Title"
  );
  const nextStep = bundle.getMessage("Checklist-MainPage-NextSteps");
  const nextStepDetails = bundle.getMessage("Checklist-MainPage-NextDetails");
  const here = bundle.getMessage("Checklist-MainPage-Link");

  const applicationHelpLink =
    stage?.slice(-2) == "mx" ? (
      <Link href="https://trabaja.amazon.com.mx/contact-us#/" target="_blank">
        {here}
      </Link>
    ) : (
      <Link href="http://amazon.com/applicationhelp" target="_blank">
        {here}
      </Link>
    );

  const bgcWaitingMessage = bundle.getMessage("Checklist-BGCStatus-Details1");
  const bgcInProgressMessage = bundle.getMessage(
    "Checklist-BGCStatus-Details2"
  );
  const bgcCompleteMessage = bundle.getMessage("Checklist-BGCStatus-Details3");
  const bgcNotRequiredMessage = bundle.getMessage(
    "Checklist-BGCStatus-Details5"
  );
  const bgcErrorMessage = bundle.formatMessage(
    "Checklist-MainPage-BGC-Error1",
    {
      supportLink: applicationHelpLink,
    }
  );
  const dtErrorMessage = bundle.formatMessage("Checklist-MainPage-DT-Error1", {
    supportLink: applicationHelpLink,
  });

  const orderYourSafetyShoesTitle = bundle.getMessage(
    "Checklist-MainPage-MindYourStep-Title"
  );

  const documentStatusTitle = bundle.getMessage(
    "Checklist-MainPage-DocumentStatus-Title"
  );

  const documentUploadTitle = bundle.getMessage(
    "Checklist-MainPage-Upload-Work-Authorization-Documents-Title"
  );

  const atozStatusTitle = bundle.getMessage("Checklist-AtoZ-AtoZStatus-Title");

  const BGCStatusDetailMessage = {
    [StatusEnum.INITIATED]: (
      <StatusIndicator
        status={Status.Waiting}
        messageText={bgcWaitingMessage}
      />
    ),
    [StatusEnum.IN_PROGRESS]: (
      <StatusIndicator
        status={Status.Waiting}
        messageText={bgcInProgressMessage}
      />
    ),
    [StatusEnum.COMPLETED]: (
      <StatusIndicator
        status={Status.Positive}
        messageText={bgcCompleteMessage}
      />
    ),
    [StatusEnum.ERROR]: (
      <MessageBanner type={MessageBannerType.Warning}>
        {bgcErrorMessage}
      </MessageBanner>
    ),
    [StatusEnum.NOT_REQUIRED]: (
      <StatusIndicator
        status={Status.Waiting}
        messageText={bgcNotRequiredMessage}
      />
    ),
  };

  const tasks: Array<Task> = [
    {
      titleText: appointmentTitle,
      details: function CompleteAppointmentDetailsTask(status: StatusEnum) {
        return <AppointmentDetailsTask status={status} />;
      },
      analyticsOnAction: "appointment details",
      hideOnConfig:
        !appConfigLoaded ||
        !isTaskEnabledByAppConfig(APPOINTMENT_DETAILS_TASK_ID, applicationId),
    },
    {
      titleText: videoTitle,
      taskName: NEW_HIRE_VIDEO,
      details: videoDetails,
      buttonText: videoButtonText,
      disableWhenCompleted: false,
      href: NEW_HIRE_VIDEO_URL,
      titleStatusPath: NEW_HIRE_VIDEO_STATUS_ENDPOINT,
      taskStatusName: "NEW_HIRE_VIDEO",
      getTaskStatusByBBId: true,
      icon: video,
      hideOnConfig:
        !appConfigLoaded ||
        !isTaskEnabledByAppConfig(NHE_VIDEO_TASK_ID, applicationId),
      duration: 5,
    },
    {
      titleText: completeI9Title,
      taskName: I_9_DOCUMENT,
      details: function CompleteI9Task(
        status: StatusEnum,
        isExpanded: boolean
      ) {
        return <I9Task isExpanded={isExpanded} />;
      },
      analyticsOnExpand: "complete employment form",
      titleStatusPath: CAAS_API_URL,
      shouldOverrideStatus: CAAS_STATUS_TO_ACTIVITY_STATUS,
      icon: form,
      hideOnConfig:
        !appConfigLoaded ||
        !isTaskEnabledByAppConfig(I9_TASK_ID, applicationId),
      duration: 5,
    },
    {
      titleText: packageI9Title,
      taskName: I_9_DOCUMENT,
      details: <Text>{packI9Detail1}</Text>,
      analyticsOnExpand: "pack documents",
      icon: form,
      hideOnConfig:
        !appConfigLoaded ||
        !isTaskEnabledByAppConfig(PACKAGE_I9_TASK_ID, applicationId),
    },
    {
      titleText: badgePhotoTitle,
      taskName: BADGE_PHOTO,
      details: badgePhotoDetails,
      analyticsOnExpand: "badge photo",
      disableWhenCompleted: true,
      buttonText: badgePhotoButtonText,
      href: TAKE_BADGE_PHOTO_START_URL,
      analyticsOnAction: "go to take badge photo",
      titleStatusPath: BADGE_PHOTO_STATUS_ENDPOINT,
      hideWhenError: true,
      expandWhenNotStarted: false,
      shouldOverrideStatus: {
        [StatusEnum.IN_PROGRESS]: DisplayNewUIComponent
          ? StatusEnum.COMPLETED
          : StatusEnum.PENDING_REVIEW,
        [StatusEnum.API_ERROR]: StatusEnum.ACTION_REQUIRED,
        [StatusEnum.NOT_STARTED]: StatusEnum.ACTION_REQUIRED,
        [StatusEnum.COMPLETED]: StatusEnum.COMPLETED,
      },
      icon: badge,
      hideOnConfig:
        !appConfigLoaded ||
        !isTaskEnabledByAppConfig(BADGE_PHOTO_TASK_ID, applicationId),
      duration: 5,
    },
    {
      titleText: badgePhotoTitle,
      taskName: BADGE_PHOTO,
      details: bpDuringAppointment,
      analyticsOnExpand: "badge photo",
      titleStatusPath: BADGE_PHOTO_STATUS_ENDPOINT,
      isFallback: true,
      icon: badge,
      hideOnConfig:
        !appConfigLoaded ||
        !isTaskEnabledByAppConfig(BP_DURING_APPOINTMENT_TASK_ID, applicationId),
    },
    {
      titleText: screenTitle,
      taskName: "Complete pre-employment screening",
      subtasks: [
        {
          titleText: medicalCheckTitle,
          taskName: "Medical Check",
          titleStatusPath: MEDICAL_CHECK_STATUS_ENDPOINT,
          details: <div></div>,
          hideOnConfig:
            !appConfigLoaded ||
            !isTaskEnabledByAppConfig(
              MEDICAL_CHECK_IN_SCREENING_TASK_ID,
              applicationId
            ),
        },
        {
          titleText: bgcTitle,
          taskName: "BGC",
          titleStatusPath:
            appConfigLoaded && isCustomerService(applicationId)
              ? BACKGROUND_CHECK_STATUS_SF_ENDPOINT
              : BACKGROUND_CHECK_STATUS_ENDPOINT,
          details: <div></div>,
          statusDetailMessage: BGCStatusDetailMessage,
          hideOnConfig:
            !appConfigLoaded ||
            !isTaskEnabledByAppConfig(
              BGC_CHECK_IN_SCREENING_TASK_ID,
              applicationId
            ),
        },
        {
          titleText: airBgcTitle,
          taskName: "AIR BGC",
          titleStatusPath: BACKGROUND_CHECK_STATUS_AIR_ENDPOINT,
          details: <div></div>,
          statusDetailMessage: BGCStatusDetailMessage,
          checkBusinessLine: true,
          hideOnConfig:
            !appConfigLoaded ||
            !isTaskEnabledByAppConfig(
              AIR_BGC_IN_SCREENING_CHECK_TASK_ID,
              applicationId
            ),
        },
        {
          titleText: dtTitle,
          taskName: "Drug Test",
          titleStatusPath: DRUG_TEST_STATUS_ENDPOINT,
          details: <div></div>,
          errorStatusDetailMessage: (
            <MessageBanner type={MessageBannerType.Warning}>
              {dtErrorMessage}
            </MessageBanner>
          ),
          hideOnConfig:
            !appConfigLoaded ||
            !isTaskEnabledByAppConfig(
              DT_IN_SCREENING_CHECK_TASK_ID,
              applicationId
            ),
        },
      ],
      details: <Text>{duringAppointment}</Text>,
      analyticsOnExpand: "pre-employment screening",
    },
    {
      titleText: picTitle,
      details: function CompletePICTask(status: StatusEnum) {
        return (
          <PICTask status={status} href={PERSONAL_INFORMATION_COLLECTION_URL} />
        );
      },
      taskStatusName: "PIC",
      taskName: "Provide personal information",
      hideWhenError: true,
      hideOnTaskStatusError: true,
      hideInProd: true,
      analyticsOnExpand: "ash expand personal information",
      hideOnConfig:
        !appConfigLoaded ||
        !isTaskEnabledByAppConfig(
          PIC_IN_SCREENING_CHECK_TASK_ID,
          applicationId
        ),
    },
    {
      titleText: orderYourSafetyShoesTitle,
      taskName: ORDER_YOUR_SAFETY_SHOE,
      details: function ZapposPurchasePromotion(
        status: StatusEnum,
        isExpanded: boolean,
        taskId: string
      ) {
        return (
          <ZapposPurchaseTask
            status={status}
            href={ZAPPOS_URL}
            taskId={taskId}
          />
        );
      },
      taskStatusName: "ZAPPOS_SHOE_PROMOTION",
      hideWhenError: true,
      hideOnTaskStatusError: true,
      getTaskStatusByBBId: true,
      analyticsOnExpand: "ash expand shoe purchase",
      checkBusinessLine: true,
      shouldOverrideStatus: {
        [StatusEnum.INELIGIBLE]: DisplayNewUIComponent
          ? StatusEnum.NOT_AVAILABLE_YET
          : StatusEnum.INELIGIBLE,
        [StatusEnum.ELIGIBLE]: DisplayNewUIComponent
          ? StatusEnum.ACTION_REQUIRED
          : StatusEnum.ELIGIBLE,
      },
      icon: shoes,
      hideOnConfig:
        !appConfigLoaded ||
        !isTaskEnabledByAppConfig(MYSP_TASK_ID, applicationId),
      duration: 15,
    },
    {
      titleText: documentStatusTitle,
      taskName: EMPLOYMENT_PAPERWORK,
      details: function EmploymentPaperwork(status: StatusEnum) {
        return (
          <DocumentStatusTask status={status} href={DOCUMENT_STATUS_ENDPOINT} />
        );
      },
      titleStatusPath: ONBASE_DOCUMENT_STATUS_ENDPOINT,
      analyticsOnExpand: "new hire docs",
      hideWhenError: true,
      hideOnTaskStatusError: true,
      shouldOverrideStatus: {
        [StatusEnum.ERROR]: DisplayNewUIComponent
          ? StatusEnum.NOT_AVAILABLE_YET
          : StatusEnum.ERROR,
      },
      icon: computer,
      hideOnConfig:
        !appConfigLoaded ||
        !isTaskEnabledByAppConfig(EMPLOYMENT_PAPERWORK_TASK_ID, applicationId),
      duration: 30,
    },
    {
      titleText: atozStatusTitle,
      taskName: CREATE_EMPLOYEE_A_TO_Z_ACCOUNT,
      details: function AtoZAccount(status: StatusEnum) {
        return <AtoZStatusTask status={status} href={ATOZ_ENDPOINT} />;
      },
      titleStatusPath: ATOZ_STATUS_ENDPOINT,
      hideWhenError: true,
      hideOnTaskStatusError: true,
      hideOnConfig:
        !appConfigLoaded || (load(HIDE_ATOZ_TASK + applicationId) as boolean),
      analyticsOnExpand: "atoz account",
      icon: badge,
      duration: 15,
    },
    {
      titleText: documentUploadTitle,
      taskName: "Upload work authorization documents",
      details: function documentUploadTask(status: StatusEnum) {
        return <DocumentUploadTask status={status} />;
      },
      titleStatusPath: RIGHT_TO_WORK_STATUS_ENDPOINT,
      hideOnConfig:
        !appConfigLoaded ||
        !isTaskEnabledByAppConfig(DOCUMENT_TASK_ID, applicationId),
    },
  ];

  const taskGroups: Array<TaskGroup> = [
    {
      name: subTitle1,
      tasks: [tasks[11], tasks[0], tasks[2], tasks[4], tasks[1], tasks[3]],
    },
    {
      name: subTitle2,
      tasks: [tasks[5], tasks[6]],
    },
    {
      name: subTitle3,
      tasks: [tasks[7], tasks[8], tasks[9], tasks[10]],
    },
  ];

  const taskGroupsForDGS: Array<TaskGroup> = [
    {
      name: subTitle1,
      tasks: [tasks[11], tasks[2], tasks[4], tasks[1]],
    },
    {
      name: subTitle2,
      tasks: [tasks[6]],
    },
    {
      name: subTitle3,
      tasks: [tasks[7], tasks[8], tasks[9], tasks[10]],
    },
  ];

  const taskGroupsForNewUI: Array<TaskGroup> = [
    {
      id: PRE_HIRE_APPOINTMENT_GROUP,
      name: newSubTitle1,
      tasks: [tasks[2], tasks[4], tasks[1]],
    },
    {
      id: FIRST_DAY_GROUP,
      name: newSubTitle3,
      tasks: [tasks[8], tasks[9], tasks[10]],
    },
  ];

  const onlyNotHiddenInProd = (task: Task) =>
    !task.hideInProd || stage !== "prod";

  const isTaskAllowedForStage = (task: Task) =>
    !task.taskName || isTaskEnabled(task.taskName, "TASK", stage ?? "prod");

  const isSubtaskaskAllowedForStage = (subtask: Task) =>
    !subtask.taskName ||
    isTaskEnabled(subtask.taskName, "SUBTASK", stage ?? "prod");

  const hideTaskOnConfig = (task: Task) => !task.hideOnConfig;

  let targetTaskGroup = isDragonstone(requisitionId)
    ? taskGroupsForDGS
    : taskGroups;

  if (DisplayNewUIComponent) {
    targetTaskGroup = taskGroupsForNewUI;
  }

  const isDragonStonePage = isDragonstone(requisitionId);
  const groups = targetTaskGroup.map((taskGroup) => {
    return {
      ...taskGroup,
      tasks: taskGroup.tasks
        .filter(onlyNotHiddenInProd)
        .filter(isTaskAllowedForStage)
        .filter(hideTaskOnConfig)
        .map((task) => ({
          ...task,
          href:
            task.href &&
            buildUrl(task.href, { stage, requisitionId, applicationId }),
          titleStatusPath:
            task.titleStatusPath &&
            buildUrl(task.titleStatusPath, {
              stage,
              requisitionId,
              applicationId,
              candidateId,
            }),
          subtaskStatuses:
            task.subtasks &&
            task.subtasks.filter(isSubtaskaskAllowedForStage).map((subTask) => {
              return {
                title: subTask.titleText,
                path: subTask.titleStatusPath
                  ? buildUrl(subTask.titleStatusPath, {
                      stage,
                      requisitionId,
                      applicationId,
                      candidateId,
                    })
                  : undefined,
              };
            }),
        })),
    };
  });

  const {
    matches: { m: isMediumViewport, s: isSmallViewport },
  } = useBreakpoints();
  const isLargeViewport = !isSmallViewport && !isMediumViewport;

  if (DisplayNewUIComponent) {
    return (
      <Col backgroundColor={newUIBackgroundColor}>
        {isSmallViewport && isDragonStonePage && (
          <Col alignItems="center">
            {appConfigLoaded && (
              <ManageApplicationCard stage={stage} collapsed={true} />
            )}
          </Col>
        )}
        <Col gridGap="2rem">
          {isDragonStonePage && (
            <Col alignItems="left">
              <AppointmentCard stage={stage} />
            </Col>
          )}
        </Col>

        <Col alignItems="center">
          <Col
            color={defaultTextColor}
            maxWidth={isLargeViewport ? "1500px" : "900px"}
            width={"100%"}
            gridGap="2rem"
            padding={
              isLargeViewport ? "1rem 1rem 4rem 1rem" : "0rem 0rem 4rem 0rem"
            }
          >
            <Flex
              flexDirection={isLargeViewport ? "row" : "column"}
              justifyContent={"space-evenly"}
              gridGap={isLargeViewport ? "1rem" : "0rem"}
            >
              <Col width={isLargeViewport ? "60%" : "100%"}>
                {stage ? (
                  <TaskGroupAggregator
                    groups={groups}
                    applicationId={applicationId}
                    candidateId={candidateId}
                    stage={stage}
                  />
                ) : null}
              </Col>
              <Col width={isLargeViewport ? "40%" : "100%"}>
                {appConfigLoaded && isDragonStonePage && <ScreeningResults />}
                {(isLargeViewport || isMediumViewport) &&
                  isDragonStonePage &&
                  appConfigLoaded && (
                    <ManageApplicationCard stage={stage} collapsed={false} />
                  )}
              </Col>
            </Flex>
          </Col>
        </Col>
      </Col>
    );
  }

  return (
    <>
      <Col gridGap="2rem">
        {isDragonStonePage && (
          <Col alignItems="left">
            <AppointmentCard stage={stage} />
          </Col>
        )}
        {appConfigLoaded && isDragonStonePage && (
          <Col gridGap="1rem">
            <ApplicationStatus />
          </Col>
        )}
      </Col>

      <Col alignItems="center">
        <Col
          color={defaultTextColor}
          maxWidth="900px"
          padding="1rem"
          gridGap="2rem"
          width="100%"
        >
          {!isDragonStonePage && (
            <Col gridGap="1rem" alignItems="center">
              <img src={headerImageSrc} alt="Amazon jobs logo" width="100%" />
              <IconCheck />
              <StencilText fontSize="T400">
                {/* Thank you for applying to Amazon! */}
                {welcomeMessage}
              </StencilText>
              <Sda />
            </Col>
          )}
          {!isDragonStonePage && (
            <Col gridGap="1rem">
              {/* <H1>Your Pre-Day 1 Checklist</H1> */}
              <H1>{title}</H1>
              <Text>
                {/* Review the information below to learn more about next steps you
                need to complete prior to your first day at Amazon. */}
                {reminderMessage}
              </Text>
            </Col>
          )}
          {isDragonStonePage && (
            <Col gridGap="1rem">
              <H1>{nextStep}</H1>
              <Text>{nextStepDetails}</Text>
            </Col>
          )}
          {stage
            ? groups.map((taskGroup, index) => (
                <TasksGroup
                  key={taskGroup.name + index}
                  data={taskGroup}
                  applicationId={applicationId}
                  candidateId={candidateId}
                  stage={stage}
                />
              ))
            : null}

          {isDragonStonePage && (
            <Col gridGap="1rem" alignItems="left">
              {appConfigLoaded && (
                <ManageApplicationCard stage={stage} collapsed={false} />
              )}
            </Col>
          )}
        </Col>
      </Col>
    </>
  );
};
